import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Send, Mail, MessageSquare, CheckCircle, XCircle, Clock,
  AlertCircle, RotateCcw, ChevronDown, ChevronRight, Bold, Italic, Underline as UIcon,
  List as ListIcon, ListOrdered, Link2, Type, Palette, Highlighter, Paperclip, X,
  Save, FolderOpen, Trash2, ThumbsUp, ThumbsDown,
} from "lucide-react";
import Layout from "../components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });

/**
 * Dedicated distribution workflow for a published call sheet.
 * Left pane: channel selection, subject, WYSIWYG email body, recipients, delivery status.
 * Right pane: LIVE preview of the email (not the call sheet).
 */
export default function DistributeCallSheet() {
  const { callsheetId } = useParams();
  const navigate = useNavigate();
  const [cs, setCs] = useState(null);
  const [project, setProject] = useState(null);
  const [crew, setCrew] = useState([]);
  const [me, setMe] = useState(null);
  const [selected, setSelected] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [subject, setSubject] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [identitySeeded, setIdentitySeeded] = useState(false);
  const [bodyHtml, setBodyHtml] = useState("");
  const [distributing, setDistributing] = useState(false);
  const [resending, setResending] = useState({});
  const [expanded, setExpanded] = useState({});
  const [logs, setLogs] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [tplMenuOpen, setTplMenuOpen] = useState(false);
  const [deliveryFilter, setDeliveryFilter] = useState("all"); // all | delivered | opened | bounced
  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);
  const tplMenuRef = useRef(null);

  const fetchLogs = () =>
    axios.get(`${API}/callsheets/${callsheetId}/distribution-logs`, ax())
      .then((r) => setLogs(r.data))
      .catch(() => {});
  const fetchRsvps = () =>
    axios.get(`${API}/callsheets/${callsheetId}/rsvps`, ax())
      .then((r) => setRsvps(r.data || []))
      .catch(() => {});

  useEffect(() => {
    axios.get(`${API}/auth/me`, ax()).then((r) => setMe(r.data)).catch(() => {});
    axios.get(`${API}/callsheets/${callsheetId}`, ax())
      .then((r) => {
        setCs(r.data);
        if (r.data?.status === "draft") {
          toast.error("Publish the call sheet before distributing.");
          navigate(`/callsheets/${callsheetId}/preview`);
          return;
        }
        if (r.data?.project_id) {
          axios.get(`${API}/projects/${r.data.project_id}`, ax()).then((p) => setProject(p.data)).catch(() => {});
          axios.get(`${API}/projects/${r.data.project_id}/crew`, ax()).then((c) => setCrew(c.data)).catch(() => {});
        } else {
          axios.get(`${API}/crew`, ax()).then((c) => setCrew(c.data)).catch(() => {});
        }
      })
      .catch(() => navigate("/dashboard"));
    fetchLogs();
    fetchRsvps();
    const interval = setInterval(() => { fetchLogs(); fetchRsvps(); }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callsheetId]);

  // Load saved email templates (user-scoped) once on mount.
  useEffect(() => {
    axios.get(`${API}/email-templates`, ax())
      .then((r) => setEmailTemplates(r.data || []))
      .catch(() => {});
  }, []);

  // Dismiss the Templates dropdown when clicking outside.
  useEffect(() => {
    if (!tplMenuOpen) return;
    const onDocClick = (e) => {
      if (tplMenuRef.current && !tplMenuRef.current.contains(e.target)) {
        setTplMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [tplMenuOpen]);

  // Only people assigned on this call sheet are eligible.
  const onSheetCrew = useMemo(() => {
    if (!cs) return [];
    const ids = new Set();
    (cs.crew_assignments || []).forEach((a) => a?.crew_id && ids.add(a.crew_id));
    (cs.cast_calls || []).forEach((a) => a?.crew_id && ids.add(a.crew_id));
    (cs.dept_calls || []).forEach((a) => a?.crew_id && ids.add(a.crew_id));
    return crew.filter((m) => ids.has(m.id));
  }, [cs, crew]);

  // Pre-select everyone + seed the editor once on load.
  const [autoSelected, setAutoSelected] = useState(false);
  useEffect(() => {
    if (autoSelected || !cs || onSheetCrew.length === 0) return;
    setSelected(onSheetCrew.map((m) => m.id));
    setAutoSelected(true);
  }, [cs, onSheetCrew, autoSelected]);

  // Seed per-send identity fields from project defaults, once.
  // Falls back to "Production Office" when the project has no default
  // (matches the server-side fallback so the preview reflects reality).
  useEffect(() => {
    if (identitySeeded || !project) return;
    setFromName((v) => v || project.default_from_name || "Production Office");
    setReplyTo((v) => v || project.default_reply_to || "");
    setIdentitySeeded(true);
  }, [project, identitySeeded]);

  useEffect(() => {
    if (!cs || bodyHtml) return;
    const defaultHtml =
      `<p>Hi {{name}},</p>` +
      `<p>Your call sheet for <strong>${escapeHtml(cs.production_name || "the shoot")}</strong>` +
      ` on <strong>{{date}}</strong> is attached.</p>` +
      `<p>Your call time is <strong>{{call_time}}</strong> at ${escapeHtml(cs.location_address || cs.location_name || "set")}.</p>` +
      `<p>Please review the attached PDF and confirm your call.</p>`;
    setBodyHtml(defaultHtml);
    if (bodyRef.current) bodyRef.current.innerHTML = defaultHtml;
  }, [cs, bodyHtml]);

  // ── RECIPIENT HELPERS ──────────────────────────────────────────────────
  const toggleAll = () => {
    if (selected.length === onSheetCrew.length) setSelected([]);
    else setSelected(onSheetCrew.map((c) => c.id));
  };
  const callTimeFor = (m) => {
    if (!cs) return "";
    const crewAsgn = (cs.crew_assignments || []).find((a) => a.crew_id === m.id);
    const castAsgn = (cs.cast_calls || []).find((a) => a.crew_id === m.id);
    return crewAsgn?.call_time || castAsgn?.arrive_time || cs.general_call || "";
  };

  // ── EDITOR TOOLBAR ─────────────────────────────────────────────────────
  const applyCmd = (cmd, value = null) => {
    bodyRef.current?.focus();
    document.execCommand(cmd, false, value);
    if (bodyRef.current) setBodyHtml(bodyRef.current.innerHTML);
  };
  const insertLink = () => {
    const url = window.prompt("Link URL:", "https://");
    if (!url) return;
    applyCmd("createLink", url);
  };
  const applyBlock = (tag) => applyCmd("formatBlock", tag);
  const applyColor = (color) => applyCmd("foreColor", color);
  const applyHighlight = (color) => {
    // Chrome/Edge prefer `hiliteColor`; Safari ignores it unless `styleWithCSS` is on.
    document.execCommand("styleWithCSS", false, true);
    applyCmd("hiliteColor", color);
    document.execCommand("styleWithCSS", false, false);
  };

  // ── EMAIL TEMPLATES (save / load / delete) ─────────────────────────────
  const loadEmailTemplate = (tpl) => {
    if (!tpl) return;
    setSubject(tpl.subject || "");
    const html = tpl.body_html || "";
    setBodyHtml(html);
    if (bodyRef.current) bodyRef.current.innerHTML = html;
    setTplMenuOpen(false);
    toast.success(`Loaded template "${tpl.name}"`);
  };
  const saveEmailTemplate = async () => {
    const name = (window.prompt("Save this email as a template. Name:") || "").trim();
    if (!name) return;
    const existing = emailTemplates.find((t) => (t.name || "").toLowerCase() === name.toLowerCase());
    try {
      if (existing) {
        if (!window.confirm(`A template named "${name}" already exists. Overwrite it?`)) return;
        const { data } = await axios.put(`${API}/email-templates/${existing.id}`,
          { name, subject, body_html: bodyHtml }, ax());
        setEmailTemplates((list) => list.map((t) => (t.id === data.id ? data : t))
          .sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      } else {
        const { data } = await axios.post(`${API}/email-templates`,
          { name, subject, body_html: bodyHtml }, ax());
        setEmailTemplates((list) => [...list, data]
          .sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      }
      setTplMenuOpen(false);
      toast.success(`Saved template "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save template");
    }
  };
  const deleteEmailTemplate = async (tpl) => {
    if (!tpl) return;
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/email-templates/${tpl.id}`, ax());
      setEmailTemplates((list) => list.filter((t) => t.id !== tpl.id));
      toast.success(`Deleted template "${tpl.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete template");
    }
  };

  // ── PREVIEW ────────────────────────────────────────────────────────────
  const previewRecipient = useMemo(() => {
    if (selected.length === 0) {
      return { name: "Crew Member", call_time: cs?.general_call || "6:00 AM", email: "crew@example.com" };
    }
    const first = onSheetCrew.find((m) => m.id === selected[0]) || onSheetCrew[0];
    return {
      name: first?.name || "Crew Member",
      call_time: callTimeFor(first) || cs?.general_call || "6:00 AM",
      email: first?.email || "crew@example.com",
    };
  }, [selected, onSheetCrew, cs]);

  const resolvedSubject = subject || `Call Sheet: ${cs?.production_name || "Production"} – ${cs?.shoot_date || ""}`;
  const resolvedBodyHtml = useMemo(() => {
    return (bodyHtml || "")
      .replaceAll("{{name}}", escapeHtml(previewRecipient.name))
      .replaceAll("{{call_time}}", escapeHtml(previewRecipient.call_time))
      .replaceAll("{{production}}", escapeHtml(cs?.production_name || ""))
      .replaceAll("{{date}}", escapeHtml(formatShootDate(cs?.shoot_date)))
      .replaceAll("{{location}}", escapeHtml(cs?.location_address || cs?.location_name || ""));
  }, [bodyHtml, previewRecipient, cs]);

  const emailHtml = useMemo(() => renderEmailHtml({
    cs, project,
    recipient: previewRecipient,
    body_html: resolvedBodyHtml,
    accent: project?.accent_color || "#F59E0B",
    logo_data_uri: cs?.logo_data_uri || project?.logo_data_uri || "",
  }), [cs, project, previewRecipient, resolvedBodyHtml]);

  // ── SEND ───────────────────────────────────────────────────────────────
  const distribute = async () => {
    if (!selected.length) return toast.error("Select at least one recipient");
    if (!sendEmail && !sendSms) return toast.error("Pick email, SMS, or both");
    setDistributing(true);
    try {
      const { data } = await axios.post(`${API}/callsheets/${callsheetId}/distribute`, {
        crew_ids: selected,
        send_email: sendEmail,
        send_sms: sendSms,
        subject,
        message: bodyHtml,
        from_name: fromName,
        reply_to: replyTo,
        attachments: sendEmail ? attachments.map((a) => ({ name: a.name, mime: a.mime, base64: a.base64 })) : [],
      }, ax());
      await fetchLogs();
      toast.success(`Sent to ${data.sent_count} recipient(s). Live delivery status appears below.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Distribution failed");
    } finally {
      setDistributing(false);
    }
  };

  const addAttachments = async (fileList) => {
    const files = Array.from(fileList || []);
    const MAX_PER = 10 * 1024 * 1024; // 10 MB per file
    const MAX_TOTAL = 25 * 1024 * 1024; // 25 MB SendGrid-safe
    const next = [...attachments];
    let runningTotal = next.reduce((a, x) => a + x.size, 0);
    for (const f of files) {
      if (f.size > MAX_PER) {
        toast.error(`"${f.name}" is larger than 10 MB`);
        continue;
      }
      if (runningTotal + f.size > MAX_TOTAL) {
        toast.error("Attachments total would exceed 25 MB");
        continue;
      }
      try {
        const base64 = await readFileAsBase64(f);
        next.push({ name: f.name, size: f.size, mime: f.type || "application/octet-stream", base64 });
        runningTotal += f.size;
      } catch {
        toast.error(`Failed to read ${f.name}`);
      }
    }
    setAttachments(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const removeAttachment = (idx) => setAttachments((a) => a.filter((_, i) => i !== idx));

  const resendOne = async (crewId, type) => {
    const key = `${crewId}:${type}`;
    setResending((s) => ({ ...s, [key]: true }));
    try {
      await axios.post(`${API}/callsheets/${callsheetId}/resend`, {
        crew_id: crewId, type, subject, message: bodyHtml,
        from_name: fromName, reply_to: replyTo,
      }, ax());
      await fetchLogs();
      toast.success(`Resent via ${type.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally {
      setResending((s) => ({ ...s, [key]: false }));
    }
  };

  // ── DELIVERY STATUS ────────────────────────────────────────────────────
  const latestByRecipient = useMemo(() => {
    const map = new Map();
    for (const l of logs) {
      const key = `${l.crew_id || l.recipient}:${l.type}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    const rows = [];
    for (const [, group] of map) {
      group.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
      const latest = group[group.length - 1];
      const timeline = [];
      for (const row of group) {
        const sendStatus = ["failed", "undelivered"].includes((row.status || "").toLowerCase())
          ? (row.status || "failed") : "sent";
        timeline.push({ event: sendStatus, ts: row.timestamp, reason: row.error || "", attempt: true });
        for (const ev of (row.events || [])) {
          timeline.push({ event: ev.event || "update", ts: ev.ts, reason: ev.reason || ev.response || "" });
        }
      }
      rows.push({ ...latest, _timeline: timeline, _attempts: group.length });
    }
    return rows.sort((a, b) => (b.updated_at || b.timestamp || "").localeCompare(a.updated_at || a.timestamp || ""));
  }, [logs]);

  // Map crew_id → latest rsvp row so each delivery row can show the response.
  const rsvpByCrewId = useMemo(() => {
    const m = {};
    (rsvps || []).forEach((r) => { if (r.crew_id) m[r.crew_id] = r; });
    return m;
  }, [rsvps]);
  const rsvpCounts = useMemo(() => {
    const c = { confirmed: 0, declined: 0, pending: 0, total: rsvps.length };
    (rsvps || []).forEach((r) => {
      if (r.status === "confirmed") c.confirmed += 1;
      else if (r.status === "declined") c.declined += 1;
      else c.pending += 1;
    });
    return c;
  }, [rsvps]);

  // Bucket a delivery row for the filter chips.
  // "opened" is a subset of "delivered"; "bounced" covers every hard failure.
  const _rowBucket = (row) => {
    const s = (row.status || "").toLowerCase();
    if (["bounced", "dropped", "blocked", "spam", "failed", "undelivered"].includes(s)) return "bounced";
    if (["opened", "clicked"].includes(s) || (row._timeline || []).some((e) => (e.event || "").toLowerCase() === "open")) return "opened";
    if (s === "delivered") return "delivered";
    return "pending";
  };
  const deliveryCounts = useMemo(() => {
    const counts = { all: latestByRecipient.length, delivered: 0, opened: 0, bounced: 0 };
    for (const r of latestByRecipient) {
      const b = _rowBucket(r);
      if (b === "opened") { counts.opened += 1; counts.delivered += 1; }
      else if (b === "delivered") { counts.delivered += 1; }
      else if (b === "bounced") { counts.bounced += 1; }
    }
    return counts;
  }, [latestByRecipient]);
  const filteredRecipients = useMemo(() => {
    if (deliveryFilter === "all") return latestByRecipient;
    return latestByRecipient.filter((r) => {
      const b = _rowBucket(r);
      if (deliveryFilter === "delivered") return b === "delivered" || b === "opened";
      if (deliveryFilter === "opened") return b === "opened";
      if (deliveryFilter === "bounced") return b === "bounced";
      return true;
    });
  }, [latestByRecipient, deliveryFilter]);

  if (!cs) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center text-amber-400 animate-pulse">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#0D0F12] text-white">
        {/* Top action bar — stays inside Layout so the global side nav remains visible */}
        <div className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-[#28303F] bg-[#0D0F12]/90 px-4 py-3 backdrop-blur">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="distribute-back-btn" onClick={() => navigate(`/callsheets/${callsheetId}/preview`)}
              className="flex items-center gap-2 text-gray-400 hover:text-white text-sm">
              <ArrowLeft size={16}/> Back to preview
            </button>
            <div className="hidden sm:block h-6 w-px bg-[#28303F]" />
            <div className="min-w-0">
              <h1 className="font-[Outfit] font-bold truncate">{cs.production_name || "Call Sheet"}</h1>
              <p className="text-xs text-gray-500">Distribute · {cs.shoot_date}</p>
            </div>
          </div>
          <button
            data-testid="distribute-send-btn"
            onClick={distribute}
            disabled={distributing || !selected.length || (!sendEmail && !sendSms)}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold px-4 py-2 rounded-lg text-sm transition-all"
          >
            <Send size={14}/> {distributing ? "Sending…" : `Send to ${selected.length}`}
          </button>
        </div>

        {/* Two-pane 50/50 split */}
        <div className="flex min-w-0 flex-col lg:flex-row">
          {/* LEFT: controls */}
          <div className="w-full lg:w-1/2 shrink-0 border-r border-[#28303F] bg-[#14181F] p-5 space-y-5">
          {/* Channel */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Send via</p>
            <div className="flex gap-2">
              <button
                data-testid="distribute-channel-email"
                onClick={() => setSendEmail((v) => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all ${sendEmail ? "bg-blue-500/20 text-blue-400 border-blue-500/30" : "border-[#28303F] text-gray-500"}`}>
                <Mail size={14}/> Email
              </button>
              <button
                data-testid="distribute-channel-sms"
                onClick={() => setSendSms((v) => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all ${sendSms ? "bg-green-500/20 text-green-400 border-green-500/30" : "border-[#28303F] text-gray-500"}`}>
                <MessageSquare size={14}/> SMS
              </button>
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Subject</label>
            <input
              data-testid="distribute-subject-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`Call Sheet: ${cs.production_name} – ${cs.shoot_date}`}
              className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Sender identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">From Name</label>
              <input
                data-testid="distribute-from-name-input"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder={project?.default_from_name || "Production Office"}
                className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider mb-1.5 block">Reply-To</label>
              <input
                type="email"
                data-testid="distribute-reply-to-input"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder={project?.default_reply_to || "producer@yourstudio.com"}
                className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-amber-500"
              />
            </div>
            <p className="col-span-full text-[10px] text-gray-600 -mt-1">
              Replies to the delivered email go to this address. Defaults come from Project Settings.
            </p>
          </div>

          {/* WYSIWYG body */}
          <div>
            <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
              <label className="text-xs text-gray-500 uppercase tracking-wider">Email body</label>
              <div className="flex items-center gap-2">
                <div ref={tplMenuRef} className="relative">
                  <button
                    type="button"
                    data-testid="email-templates-btn"
                    onClick={() => setTplMenuOpen((o) => !o)}
                    className="flex items-center gap-1.5 bg-[#0D0F12] hover:bg-[#1A1F29] text-gray-300 border border-[#28303F] px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors"
                  >
                    <FolderOpen size={12} /> Templates <ChevronDown size={11} />
                  </button>
                  {tplMenuOpen && (
                    <div
                      data-testid="email-templates-menu"
                      className="absolute right-0 top-full mt-1 w-64 z-40 bg-[#14181F] border border-[#28303F] rounded-lg shadow-xl py-1"
                    >
                      <button
                        type="button"
                        data-testid="email-template-save-btn"
                        onClick={saveEmailTemplate}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-300 hover:bg-[#1E2530]"
                      >
                        <Save size={12} /> Save current email as template…
                      </button>
                      <div className="my-1 border-t border-[#28303F]" />
                      {emailTemplates.length === 0 ? (
                        <p className="px-3 py-2 text-[11px] text-gray-500">No saved templates yet.</p>
                      ) : (
                        <>
                          <p className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-gray-500">Load template</p>
                          {emailTemplates.map((tpl) => (
                            <div
                              key={tpl.id}
                              data-testid={`email-template-row-${tpl.id}`}
                              className="group flex items-center justify-between gap-2 px-3 py-1.5 hover:bg-[#1E2530]"
                            >
                              <button
                                type="button"
                                data-testid={`email-template-load-${tpl.id}`}
                                onClick={() => loadEmailTemplate(tpl)}
                                className="flex-1 min-w-0 text-left text-xs text-gray-200 truncate"
                                title={tpl.name}
                              >
                                {tpl.name}
                              </button>
                              <button
                                type="button"
                                data-testid={`email-template-delete-${tpl.id}`}
                                onClick={() => deleteEmailTemplate(tpl)}
                                title="Delete template"
                                className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <span className="hidden md:inline text-[10px] text-gray-600">Use {"{{name}}"}, {"{{call_time}}"}, {"{{production}}"}, {"{{date}}"}, {"{{location}}"}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 bg-[#0D0F12] border border-[#28303F] border-b-0 rounded-t-lg px-2 py-1.5 flex-wrap">
              <BlockPicker testid="wysiwyg-block" onPick={applyBlock} />
              <span className="w-px h-4 bg-[#28303F] mx-1" />
              <ToolbarBtn testid="wysiwyg-bold" title="Bold" onClick={() => applyCmd("bold")}><Bold size={13}/></ToolbarBtn>
              <ToolbarBtn testid="wysiwyg-italic" title="Italic" onClick={() => applyCmd("italic")}><Italic size={13}/></ToolbarBtn>
              <ToolbarBtn testid="wysiwyg-underline" title="Underline" onClick={() => applyCmd("underline")}><UIcon size={13}/></ToolbarBtn>
              <span className="w-px h-4 bg-[#28303F] mx-1" />
              <ColorPicker
                testid="wysiwyg-color"
                title="Text color"
                icon={<Type size={13}/>}
                onPick={applyColor}
                swatches={["#111111", "#555555", "#D97706", "#DC2626", "#16A34A", "#2563EB", "#7C3AED", "#DB2777"]}
              />
              <ColorPicker
                testid="wysiwyg-highlight"
                title="Highlight color"
                icon={<Highlighter size={13}/>}
                onPick={applyHighlight}
                swatches={["#FEF3C7", "#FEE2E2", "#DCFCE7", "#DBEAFE", "#EDE9FE", "#FCE7F3", "#FDE68A", "transparent"]}
              />
              <span className="w-px h-4 bg-[#28303F] mx-1" />
              <ToolbarBtn testid="wysiwyg-ul" title="Bulleted list" onClick={() => applyCmd("insertUnorderedList")}><ListIcon size={13}/></ToolbarBtn>
              <ToolbarBtn testid="wysiwyg-ol" title="Numbered list" onClick={() => applyCmd("insertOrderedList")}><ListOrdered size={13}/></ToolbarBtn>
              <ToolbarBtn testid="wysiwyg-link" title="Insert link" onClick={insertLink}><Link2 size={13}/></ToolbarBtn>
            </div>
            <div
              ref={bodyRef}
              data-testid="distribute-body-editor"
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => setBodyHtml(e.currentTarget.innerHTML)}
              className="min-h-[140px] max-h-[260px] overflow-y-auto bg-[#0D0F12] border border-[#28303F] border-t-0 rounded-b-lg px-3 py-2 text-xs text-gray-200 focus:outline-none focus:border-amber-500 prose prose-invert prose-xs max-w-none [&>p]:my-1"
            />
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Attachments ({attachments.length})</p>
              <button
                data-testid="distribute-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300">
                <Paperclip size={13} /> Add files
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                data-testid="distribute-attach-input"
                onChange={(e) => addAttachments(e.target.files)}
              />
            </div>
            {attachments.length === 0 ? (
              <p className="text-[11px] text-gray-600 py-2">Attach additional files (max 10 MB each · 25 MB total).</p>
            ) : (
              <div className="space-y-1">
                {attachments.map((a, i) => (
                  <div key={i} data-testid="distribute-attach-item"
                    className="flex items-center justify-between gap-2 bg-[#0D0F12] border border-[#28303F] rounded px-2.5 py-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-200 truncate">{a.name}</p>
                      <p className="text-[10px] text-gray-600">{formatFileSize(a.size)} · {a.mime}</p>
                    </div>
                    <button onClick={() => removeAttachment(i)}
                      data-testid="distribute-attach-remove"
                      className="text-gray-500 hover:text-red-400 shrink-0 p-1">
                      <X size={13} />
                    </button>
                  </div>
                ))}
                <p className="text-[10px] text-gray-600 pt-1">
                  The published call sheet PDF is always attached automatically — these are extra.
                </p>
              </div>
            )}
          </div>

          {/* Recipients */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Recipients ({selected.length}/{onSheetCrew.length})</p>
              <button onClick={toggleAll} className="text-xs text-amber-400 hover:text-amber-300">
                {selected.length === onSheetCrew.length ? "Deselect All" : "Select All"}
              </button>
            </div>
            {onSheetCrew.length === 0 ? (
              <p className="text-gray-600 text-xs py-4 text-center">No crew or talent assigned to this call sheet yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {onSheetCrew.map((m) => {
                  const isSel = selected.includes(m.id);
                  const t = callTimeFor(m);
                  return (
                    <button key={m.id} data-testid="recipient-checkbox"
                      onClick={() => setSelected((s) => s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id])}
                      className={`w-full flex items-center justify-between gap-2 p-2.5 rounded-lg border text-left transition-all ${isSel ? "bg-amber-500/10 border-amber-500/30" : "border-[#28303F] hover:border-[#404854]"}`}>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${isSel ? "text-white" : "text-gray-400"}`}>{m.name}</p>
                        <p className="text-xs text-gray-600 truncate">{m.role} {m.email && `· ${m.email}`}</p>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        {t && <p className="text-xs text-amber-400 font-mono whitespace-nowrap">{t}</p>}
                        <div className={`w-4 h-4 rounded border-2 ml-auto mt-1 ${isSel ? "bg-amber-500 border-amber-500" : "border-[#28303F]"}`}/>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Delivery status (post-send) */}
          {latestByRecipient.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Delivery Status ({latestByRecipient.length})</p>
                <button onClick={() => { fetchLogs(); fetchRsvps(); }} data-testid="delivery-refresh-btn" className="text-xs text-amber-400 hover:text-amber-300">Refresh</button>
              </div>
              {rsvpCounts.total > 0 && (
                <div
                  data-testid="rsvp-summary"
                  className="flex items-center gap-3 mb-2 px-3 py-2 rounded-md border border-[#28303F] bg-[#0D0F12] text-[11px]"
                >
                  <span className="text-gray-400 uppercase tracking-wider text-[10px]">RSVP:</span>
                  <span className="flex items-center gap-1 text-emerald-400" data-testid="rsvp-count-confirmed">
                    <ThumbsUp size={11}/> {rsvpCounts.confirmed} confirmed
                  </span>
                  <span className="flex items-center gap-1 text-red-400" data-testid="rsvp-count-declined">
                    <ThumbsDown size={11}/> {rsvpCounts.declined} declined
                  </span>
                  <span className="flex items-center gap-1 text-gray-500" data-testid="rsvp-count-pending">
                    <Clock size={11}/> {rsvpCounts.pending} pending
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="delivery-filter-chips">
                {[
                  { key: "all", label: "All", count: deliveryCounts.all },
                  { key: "delivered", label: "Delivered", count: deliveryCounts.delivered },
                  { key: "opened", label: "Opened", count: deliveryCounts.opened },
                  { key: "bounced", label: "Bounced", count: deliveryCounts.bounced },
                ].map((chip) => {
                  const active = deliveryFilter === chip.key;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      data-testid={`delivery-filter-${chip.key}`}
                      onClick={() => setDeliveryFilter(chip.key)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? "bg-amber-500/15 border-amber-500/60 text-amber-200"
                          : "bg-[#0D0F12] border-[#28303F] text-gray-400 hover:text-gray-200 hover:border-[#3A4556]"
                      }`}
                    >
                      {chip.label}
                      <span className={`ml-1.5 text-[10px] ${active ? "text-amber-300/80" : "text-gray-500"}`}>{chip.count}</span>
                    </button>
                  );
                })}
              </div>
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {filteredRecipients.length === 0 ? (
                  <p data-testid="delivery-empty-state" className="text-[11px] text-gray-500 px-2 py-3">
                    No recipients match this filter.
                  </p>
                ) : filteredRecipients.map((l) => (
                  <DeliveryRow key={`${l.crew_id || l.recipient}:${l.type}`}
                    row={l}
                    rsvp={rsvpByCrewId[l.crew_id]}
                    isOpen={!!expanded[`${l.crew_id || l.recipient}:${l.type}`]}
                    onToggle={() => setExpanded((s) => ({ ...s, [`${l.crew_id || l.recipient}:${l.type}`]: !s[`${l.crew_id || l.recipient}:${l.type}`] }))}
                    onResend={() => resendOne(l.crew_id, l.type)}
                    resending={!!resending[`${l.crew_id}:${l.type}`]}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

          {/* RIGHT: live email preview */}
          <div className="flex-1 min-w-0 lg:w-1/2 p-5 bg-[#0A0C10]">
            <div className="mx-auto max-w-[640px]">
              <div className="mb-4 rounded-lg border border-[#28303F] bg-[#14181F] px-4 py-3 text-xs text-gray-400">
                <p>
                  <span className="text-gray-600">From:</span>{" "}
                  {fromName ? <><span className="text-gray-200">{fromName}</span> &lt;{me?.email || "you@get2set.com"}&gt;</> : (me?.email || "you@get2set.com")}
                </p>
                <p><span className="text-gray-600">To:</span> {previewRecipient.email}</p>
                {replyTo && <p><span className="text-gray-600">Reply-To:</span> <span className="text-gray-200">{replyTo}</span></p>}
                <p><span className="text-gray-600">Subject:</span> <span className="text-gray-200">{resolvedSubject}</span></p>
              </div>
              <div
                data-testid="distribute-email-preview"
                className="rounded-lg overflow-hidden bg-white shadow-2xl"
                dangerouslySetInnerHTML={{ __html: emailHtml }}
              />
              <p className="mt-3 text-[10px] text-gray-600 text-center">
                Preview populated with the first selected recipient's details. The real email also includes a PDF attachment of the published call sheet.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────
function ToolbarBtn({ children, title, onClick, testid }) {
  return (
    <button type="button" title={title} data-testid={testid} onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-[#1a1f29]">
      {children}
    </button>
  );
}

function ColorPicker({ icon, title, swatches, onPick, testid }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={wrapRef} className="relative">
      <button type="button" title={title} data-testid={testid}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-[#1a1f29] flex items-center gap-0.5">
        {icon}
        <ChevronDown size={10} className="opacity-60" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 p-2 rounded-lg border border-[#28303F] bg-[#14181F] shadow-xl flex gap-2 whitespace-nowrap"
             data-testid={`${testid}-swatches`}>
          {swatches.map((c) => (
            <button key={c} type="button" title={c}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(c); setOpen(false); }}
              className="w-7 h-7 rounded border border-[#28303F] hover:scale-110 transition-transform shrink-0"
              style={{
                background: c === "transparent" ? "repeating-linear-gradient(45deg,#444,#444 2px,#111 2px,#111 4px)" : c,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BlockPicker({ onPick, testid }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const options = [
    { tag: "p", label: "Paragraph", cls: "text-xs" },
    { tag: "h3", label: "Heading 3", cls: "text-sm font-semibold" },
    { tag: "h2", label: "Heading 2", cls: "text-base font-bold" },
    { tag: "h1", label: "Heading 1", cls: "text-lg font-bold" },
  ];
  return (
    <div ref={wrapRef} className="relative">
      <button type="button" title="Text size" data-testid={testid}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-300 bg-[#14181F] border border-[#28303F] hover:border-amber-500/50">
        Size <ChevronDown size={11} className="opacity-70" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 w-40 py-1 rounded-lg border border-[#28303F] bg-[#14181F] shadow-xl"
             data-testid={`${testid}-options`}>
          {options.map((o) => (
            <button key={o.tag} type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(o.tag); setOpen(false); }}
              data-testid={`${testid}-opt-${o.tag}`}
              className={`w-full text-left px-3 py-1.5 text-gray-200 hover:bg-amber-500/10 hover:text-white ${o.cls}`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DeliveryRow({ row, rsvp, isOpen, onToggle, onResend, resending }) {
  const status = (row.status || "").toLowerCase();
  const isFailed = ["failed", "bounced", "dropped", "blocked", "spam", "undelivered"].includes(status);
  const isDelivered = ["delivered", "opened", "clicked"].includes(status);
  const isPending = ["sending", "sent", "queued", "deferred"].includes(status);
  const color = isFailed ? "text-red-400" : isDelivered ? "text-emerald-400" : isPending ? "text-amber-400" : "text-gray-400";
  const icon = isFailed ? <AlertCircle size={11}/> : isDelivered ? <CheckCircle size={11}/> : isPending ? <Clock size={11}/> : <XCircle size={11}/>;
  const timeline = row._timeline || [];
  // Only email channel has an RSVP (confirm/decline links are email-only).
  const showRsvp = rsvp && row.type === "email";
  const rsvpStatus = (rsvp?.status || "").toLowerCase();
  const rsvpClass = rsvpStatus === "confirmed"
    ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/40"
    : rsvpStatus === "declined"
    ? "text-red-300 bg-red-500/10 border-red-500/40"
    : "text-gray-400 bg-[#14181F] border-[#28303F]";
  const rsvpIcon = rsvpStatus === "confirmed" ? <ThumbsUp size={10}/>
    : rsvpStatus === "declined" ? <ThumbsDown size={10}/>
    : <Clock size={10}/>;
  const rsvpLabel = rsvpStatus === "confirmed" ? "Confirmed"
    : rsvpStatus === "declined" ? "Declined"
    : "Awaiting reply";
  return (
    <div data-testid="distribution-status-badge" className="text-xs bg-[#0D0F12] border border-[#28303F] rounded">
      <button onClick={onToggle} className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left hover:bg-white/[0.02]">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {isOpen ? <ChevronDown size={12} className="text-gray-500 shrink-0"/> : <ChevronRight size={12} className="text-gray-500 shrink-0"/>}
          <div className="min-w-0 flex-1">
            <p className="text-gray-200 truncate">{row.name} <span className="text-gray-600">({row.type})</span>{row._attempts > 1 && <span className="ml-1 text-[9px] text-gray-500">· {row._attempts} attempts</span>}</p>
            <p className="text-[10px] text-gray-600 truncate">{row.recipient}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
          {showRsvp && (
            <span
              data-testid={`rsvp-badge-${rsvp.crew_id}`}
              title={rsvp.responded_at ? `Responded ${new Date(rsvp.responded_at).toLocaleString()}` : "Not responded yet"}
              className={`hidden sm:inline-flex items-center gap-1 border px-1.5 py-0.5 rounded-full text-[10px] ${rsvpClass}`}
            >
              {rsvpIcon} {rsvpLabel}
            </span>
          )}
          <span className={`${color} flex items-center gap-1 capitalize whitespace-nowrap`}>{icon} {status || "unknown"}</span>
          {isFailed && row.crew_id && (
            <button
              data-testid="resend-recipient-btn"
              onClick={onResend}
              disabled={resending}
              className="flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-bold text-amber-400 hover:bg-amber-500/20 disabled:opacity-50 whitespace-nowrap">
              <RotateCcw size={10}/> {resending ? "..." : "Resend"}
            </button>
          )}
        </div>
      </button>
      {isOpen && (
        <div className="border-t border-[#28303F] bg-[#0A0C10] px-3 py-2">
          {timeline.length === 0 ? (
            <p className="text-[10px] text-gray-600">No events recorded yet.</p>
          ) : (
            <ol className="relative space-y-2 pl-3">
              {timeline.map((ev, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-3 top-1 h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                  <div className="flex items-start justify-between gap-2">
                    <p className={`capitalize text-[11px] font-medium`}>{(ev.event || "update").replace(/_/g, " ")}{ev.attempt && <span className="ml-1 text-[9px] text-gray-500 font-normal">· send attempt</span>}</p>
                    <p className="text-[10px] text-gray-600 whitespace-nowrap">{ev.ts ? new Date(ev.ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</p>
                  </div>
                  {ev.reason && <p className="text-[10px] text-gray-500 truncate" title={ev.reason}>{ev.reason}</p>}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

function escapeHtml(s) {
  return String(s || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      // reader.result is a data URI like "data:mime;base64,XXXX" — strip the prefix.
      const result = reader.result || "";
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatShootDate(raw) {
  if (!raw) return "";
  // Accept ISO (YYYY-MM-DD) or anything Date can parse. Use UTC to avoid the
  // off-by-one that bites `new Date("YYYY-MM-DD")` in negative-offset TZs.
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

// Mirrors the backend SendGrid HTML template so the right-pane preview matches
// what the recipient will actually see (minus the real RSVP tokens). An extra
// margin on <p> is injected ONLY in the preview to simulate the way Gmail /
// Apple Mail render inline paragraphs — the real email HTML uses natural flow.
function renderEmailHtml({ cs, project, recipient, body_html, accent, logo_data_uri }) {
  // Header is now painted in the accent color; the "CALL SHEET" title and
  // production-name subtitle flip to white on dark accents and black on
  // light accents so they always read.
  const headerBg = accent || "#F59E0B";
  const headerFg = isDarkColor(headerBg) ? "#ffffff" : "#111111";
  const shoot = formatShootDate(cs?.shoot_date);
  const factTable = `
    <table style="width:100%;border-collapse:collapse;margin:0 0 18px;">
      <tr style="background:#f0f0f0;">
        <td style="padding:10px 12px;border:1px solid #ccc;font-weight:bold;width:40%;">Shoot Date</td>
        <td style="padding:10px 12px;border:1px solid #ccc;">${escapeHtml(shoot)}</td>
      </tr>
      <tr>
        <td style="padding:10px 12px;border:1px solid #ccc;font-weight:bold;">Your Call Time</td>
        <td style="padding:10px 12px;border:1px solid #ccc;font-size:20px;color:#D97706;"><strong>${escapeHtml(recipient.call_time)}</strong></td>
      </tr>
    </table>`;
  const rsvp = `
    <div style="text-align:center;margin:20px 0;padding:18px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;">
      <p style="margin:0 0 12px;font-weight:bold;color:#111;">Please confirm your call:</p>
      <a href="#" style="display:inline-block;background:#16a34a;color:#fff;text-decoration:none;font-weight:bold;padding:11px 24px;border-radius:6px;margin:4px;">✓ CONFIRM</a>
      <a href="#" style="display:inline-block;background:#dc2626;color:#fff;text-decoration:none;font-weight:bold;padding:11px 24px;border-radius:6px;margin:4px;">✕ DECLINE</a>
      <p style="margin:12px 0 0;color:#6b7280;font-size:11px;">One click — your AD will be notified instantly.</p>
    </div>`;
  return `
    <style>.yc-email-body p { margin: 0 0 1.1em; }</style>
    <div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:20px;color:#111;">
      <div style="background:${headerBg};padding:24px;border-radius:8px 8px 0 0;text-align:center;">
        <h1 style="margin:0;color:${headerFg};font-size:22px;letter-spacing:2px;">CALL SHEET</h1>
        <p style="margin:6px 0 0;color:${headerFg};font-size:16px;opacity:0.92;">${escapeHtml(cs?.production_name || "")}</p>
      </div>
      <div class="yc-email-body" style="background:#f8f9fa;padding:24px;border:1px solid #dee2e6;border-top:0;">
        ${factTable}
        ${body_html}
        ${rsvp}
        <p style="color:#555;">Please find the full call sheet attached as a PDF.</p>
        <p style="color:#888;font-size:13px;">This call sheet is confidential. Please do not distribute.</p>
      </div>
      <div style="background:#f0f0f0;padding:10px;text-align:center;border-radius:0 0 8px 8px;font-size:12px;color:#666;">
        Sent via Get2Set &nbsp;|&nbsp; ${escapeHtml(project?.name || cs?.production_name || "")}
      </div>
    </div>`;
}

function isDarkColor(hex) {
  if (!hex || typeof hex !== "string") return true;
  const h = hex.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(h)) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Perceived luminance per Rec. 601 — < 128 reads as "dark" against light text.
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq < 140;
}
